import { useCallback } from 'react';
import { useTheme } from 'styled-components';

import { ServiceLocation } from '@dreryk/egabinet-healthcare-provider-frontend-api';

import {
   InteractiveTable,
   InteractiveTableSelectorItemFilter,
   InteractiveTableSelectorItemKeyExtractor,
   InteractiveTableSelectorItemRenderer,
   InteractiveTableSelectorMapper,
   InteractiveTableSelectorTableKeyExtractor,
   Text,
} from '@dreryk/react-components';
import { addressFormatter } from '@dreryk/react-common';

import { receptionServiceTranslationKeys } from '../../../../../../translations';
import { WithMultipleServiceLocationController, WithMultipleServiceLocations } from '../serviceLocations.types';

const t = receptionServiceTranslationKeys.shared.form.components.serviceLocationsInteractiveTable.selector;

type ServiceLocationsInteractiveTableSelectorProps = Pick<
   WithMultipleServiceLocationController,
   'register' | 'control' | 'setValue'
> & {
   readonly serviceLocations: ServiceLocation[];
   readonly readOnly?: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const ServiceLocationsInteractiveTableSelector = (
   props: ServiceLocationsInteractiveTableSelectorProps
): JSX.Element => {
   const theme = useTheme();
   const itemRenderer = useCallback<InteractiveTableSelectorItemRenderer<ServiceLocation>>(
      (serviceLocation, Highlight) => {
         return (
            <span>
               <Highlight node={<Text testId={'name'}>{serviceLocation.name}</Text>} />
               {', '}
               <Highlight
                  node={
                     <Text testId={'address'} color={theme.placeholder}>
                        {addressFormatter(serviceLocation.address)}
                     </Text>
                  }
               />
            </span>
         );
      },
      [theme.placeholder]
   );

   const itemFilter = useCallback<InteractiveTableSelectorItemFilter<ServiceLocation>>((serviceLocation) => {
      return [serviceLocation.name, addressFormatter(serviceLocation.address)!];
   }, []);

   const mapper = useCallback<InteractiveTableSelectorMapper<ServiceLocation, ServiceLocation>>((serviceLocation) => {
      return serviceLocation;
   }, []);

   const tableKeyExtractor = useCallback<InteractiveTableSelectorTableKeyExtractor<ServiceLocation>>(
      (serviceLocation) => {
         return serviceLocation.id.asString();
      },
      []
   );

   const itemKeyExtractor = useCallback<InteractiveTableSelectorItemKeyExtractor<ServiceLocation>>(
      (serviceLocation) => {
         return serviceLocation.id.asString();
      },
      []
   );

   return (
      <InteractiveTable.Selector<ServiceLocation, ServiceLocation, WithMultipleServiceLocations>
         register={props.register}
         testId={'serviceLocationsSelector'}
         name={'serviceLocations.selector'}
         label={t.label}
         placeholder={t.placeholder}
         items={props.serviceLocations}
         itemKeyExtractor={itemKeyExtractor}
         tableKeyExtractor={tableKeyExtractor}
         itemRenderer={itemRenderer}
         itemFilter={itemFilter}
         mapper={mapper}
         disabled={props.readOnly}
         required
      />
   );
};
